import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Bell, BellOff, CheckCheck, Inbox, Mail } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { timeAgo, fmtDate } from "@/lib/format";
import { EmptyState, PageHeader, StatusBadge } from "@/components/common";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";

export default function Notifications() {
  const [notifs, setNotifs] = useState(null);
  const [inquiries, setInquiries] = useState(null);
  const navigate = useNavigate();

  const load = useCallback(() => {
    api.get("/notifications").then(({ data }) => setNotifs(data)).catch((e) => toast.error(errMsg(e)));
    api.get("/inquiries").then(({ data }) => setInquiries(data.items)).catch(() => {});
  }, []);

  useEffect(() => { load(); }, [load]);

  const markRead = async (n) => {
    try {
      await api.put(`/notifications/${n.id}/read`);
      if (n.link) navigate(n.link);
      load();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  const markAll = async () => {
    try {
      await api.put("/notifications/read-all");
      toast.success("All notifications marked as read");
      load();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  const setInquiryStatus = async (inq, status) => {
    try {
      await api.put(`/inquiries/${inq.id}`, { status });
      toast.success(`Marked as ${status}`);
      load();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  return (
    <div data-testid="notifications-page">
      <PageHeader title="Notifications" description="Inquiries, appointments, and practice activity." testId="notifications-header">
        <Button variant="outline" onClick={markAll} data-testid="mark-all-read-btn" className="rounded-none uppercase tracking-wider text-xs font-semibold">
          <CheckCheck className="h-4 w-4 mr-1.5" /> Mark All Read
        </Button>
      </PageHeader>

      <Tabs defaultValue="inquiries">
        <TabsList className="bg-transparent border-b border-border rounded-none w-full justify-start h-auto p-0 gap-1">
          <TabsTrigger value="inquiries" data-testid="notif-tab-inquiries"
            className="rounded-none border-b-2 border-transparent data-[state=active]:border-gold data-[state=active]:text-navy data-[state=active]:bg-transparent data-[state=active]:shadow-none px-4 py-3 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
            Inquiries {inquiries?.filter((i) => i.status === "new").length > 0 && <span className="ml-1.5 px-1.5 py-0.5 bg-gold text-navy text-[10px] font-bold rounded-full">{inquiries.filter((i) => i.status === "new").length}</span>}
          </TabsTrigger>
          <TabsTrigger value="activity" data-testid="notif-tab-activity"
            className="rounded-none border-b-2 border-transparent data-[state=active]:border-gold data-[state=active]:text-navy data-[state=active]:bg-transparent data-[state=active]:shadow-none px-4 py-3 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
            Activity {notifs?.unread > 0 && <span className="ml-1.5 px-1.5 py-0.5 bg-gold text-navy text-[10px] font-bold rounded-full">{notifs.unread}</span>}
          </TabsTrigger>
        </TabsList>

        <TabsContent value="inquiries" className="mt-6">
          {!inquiries ? (
            <div className="space-y-3">{[...Array(3)].map((_, i) => <div key={i} className="h-28 bg-white border border-border animate-pulse" />)}</div>
          ) : inquiries.length === 0 ? (
            <EmptyState icon={Inbox} title="No inquiries yet" description="Contact and consultation requests from the public site appear here." testId="inquiries-empty" />
          ) : (
            <div className="space-y-3" data-testid="inquiries-list">
              {inquiries.map((inq) => (
                <div key={inq.id} className={`bg-white border p-5 sm:p-6 ${inq.status === "new" ? "border-l-4 border-l-gold" : "border-border"}`} data-testid={`inquiry-${inq.id}`}>
                  <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-4">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-3 flex-wrap">
                        <Mail className="h-4 w-4 text-gold-700 shrink-0" />
                        <h3 className="font-semibold text-navy">{inq.name}</h3>
                        <span className="text-xs uppercase tracking-wider text-muted-foreground font-semibold">
                          {inq.type === "consultation" ? "Consultation Request" : "Contact Message"}
                        </span>
                        <StatusBadge status={inq.status} />
                      </div>
                      <p className="text-sm text-foreground mt-3 leading-relaxed">{inq.message}</p>
                      <div className="flex flex-wrap gap-x-5 gap-y-1 mt-3 text-xs text-muted-foreground">
                        <span>{inq.email}</span>
                        {inq.phone && <span>{inq.phone}</span>}
                        {inq.practice_area && <span className="text-gold-700 font-semibold">{inq.practice_area}</span>}
                        {inq.preferred_date && <span>Prefers {fmtDate(inq.preferred_date)}{inq.preferred_time ? ` at ${inq.preferred_time}` : ""}</span>}
                        <span>{timeAgo(inq.created_at)}</span>
                      </div>
                    </div>
                    <div className="flex gap-2 shrink-0">
                      {inq.status === "new" && (
                        <Button size="sm" variant="outline" onClick={() => setInquiryStatus(inq, "responded")} data-testid={`inquiry-respond-${inq.id}`} className="rounded-none text-xs uppercase tracking-wider font-semibold">
                          Mark Responded
                        </Button>
                      )}
                      {inq.status !== "archived" && (
                        <Button size="sm" variant="ghost" onClick={() => setInquiryStatus(inq, "archived")} data-testid={`inquiry-archive-${inq.id}`} className="text-xs uppercase tracking-wider text-muted-foreground">
                          Archive
                        </Button>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </TabsContent>

        <TabsContent value="activity" className="mt-6">
          {!notifs ? (
            <div className="space-y-3">{[...Array(3)].map((_, i) => <div key={i} className="h-20 bg-white border border-border animate-pulse" />)}</div>
          ) : notifs.items.length === 0 ? (
            <EmptyState icon={BellOff} title="All caught up" description="New activity across your practice will appear here." testId="notifications-empty" />
          ) : (
            <div className="bg-white border border-border divide-y divide-border" data-testid="notifications-list">
              {notifs.items.map((n) => (
                <button key={n.id} onClick={() => markRead(n)} data-testid={`notification-${n.id}`}
                  className={`w-full text-left px-6 py-4 flex gap-4 hover:bg-muted/50 transition-colors ${!n.read ? "bg-gold-100/30" : ""}`}>
                  <span className={`h-9 w-9 flex items-center justify-center shrink-0 ${!n.read ? "bg-gold text-navy" : "bg-muted text-muted-foreground"}`}>
                    <Bell className="h-4 w-4" />
                  </span>
                  <div className="flex-1 min-w-0">
                    <p className={`text-sm ${!n.read ? "font-semibold text-navy" : "text-foreground"}`}>{n.title}</p>
                    {n.body && <p className="text-xs text-muted-foreground mt-0.5">{n.body}</p>}
                    <p className="text-xs text-muted-foreground mt-1">{timeAgo(n.created_at)}</p>
                  </div>
                  {!n.read && <span className="h-2 w-2 bg-gold mt-2 shrink-0 rounded-full" />}
                </button>
              ))}
            </div>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}
